import _ from 'lodash';

import { buildExpressionTree } from 'utils/expressions';
import { jsonStringify } from 'utils/json';

export const EXPLAIN_STATUS = {
  EMPTY: 'empty',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const MARKDOWN_SEGMENT_TYPE = {
  TEXT: 'text',
  BOLD: 'bold',
  CODE: 'code',
};

export const MARKDOWN_BLOCK_TYPE = {
  PARAGRAPH: 'paragraph',
  LIST: 'list',
};

const LIST_ITEM_PREFIX = '- ';

const MARKDOWN_TOKEN_REGEX = /\*\*(.+?)\*\*|`(.+?)`/g;

const buildMarkedSegment = match => {
  const [, boldContent, codeContent] = match;

  if (!_.isUndefined(boldContent)) {
    return { type: MARKDOWN_SEGMENT_TYPE.BOLD, value: boldContent };
  }

  return { type: MARKDOWN_SEGMENT_TYPE.CODE, value: codeContent };
};

/**
 * Parses the inline subset of markdown the Explain prompt emits — bold
 * (`**...**`) and inline code (`` `...` ``) — into a flat array of typed
 * segments. Block-level structure (paragraphs, bullet lists) is handled
 * separately by `parseMarkdownBlocks`; this function operates on a single
 * line / span of inline text. We deliberately do NOT pull in a full
 * markdown library (no headings, links, tables, raw HTML).
 */
export const parseInlineMarkdown = text => {
  if (_.isEmpty(text)) {
    return [];
  }

  const matches = Array.from(text.matchAll(MARKDOWN_TOKEN_REGEX));

  if (_.isEmpty(matches)) {
    return [{ type: MARKDOWN_SEGMENT_TYPE.TEXT, value: text }];
  }

  const result = matches.reduce(
    (acc, match) => {
      if (match.index > acc.cursor) {
        acc.segments.push({
          type: MARKDOWN_SEGMENT_TYPE.TEXT,
          value: text.slice(acc.cursor, match.index),
        });
      }

      acc.segments.push(buildMarkedSegment(match));
      acc.cursor = match.index + match[0].length;

      return acc;
    },
    { segments: [], cursor: 0 },
  );

  if (result.cursor < text.length) {
    result.segments.push({
      type: MARKDOWN_SEGMENT_TYPE.TEXT,
      value: text.slice(result.cursor),
    });
  }

  return result.segments;
};

const isListLine = line => line.startsWith(LIST_ITEM_PREFIX);

const finalizeBlock = acc => {
  if (acc.pendingType === MARKDOWN_BLOCK_TYPE.PARAGRAPH && !_.isEmpty(acc.pendingParagraphLines)) {
    acc.blocks.push({
      type: MARKDOWN_BLOCK_TYPE.PARAGRAPH,
      segments: parseInlineMarkdown(acc.pendingParagraphLines.join(' ')),
    });
  }

  if (acc.pendingType === MARKDOWN_BLOCK_TYPE.LIST && !_.isEmpty(acc.pendingListItems)) {
    acc.blocks.push({
      type: MARKDOWN_BLOCK_TYPE.LIST,
      items: acc.pendingListItems.map(parseInlineMarkdown),
    });
  }

  acc.pendingType = null;
  acc.pendingParagraphLines = [];
  acc.pendingListItems = [];
};

const processBlockLine = (acc, rawLine) => {
  const line = rawLine.trim();

  if (_.isEmpty(line)) {
    finalizeBlock(acc);

    return acc;
  }

  if (isListLine(line)) {
    if (acc.pendingType === MARKDOWN_BLOCK_TYPE.PARAGRAPH) {
      finalizeBlock(acc);
    }

    acc.pendingType = MARKDOWN_BLOCK_TYPE.LIST;
    acc.pendingListItems.push(line.slice(LIST_ITEM_PREFIX.length));

    return acc;
  }

  if (acc.pendingType === MARKDOWN_BLOCK_TYPE.LIST) {
    finalizeBlock(acc);
  }

  acc.pendingType = MARKDOWN_BLOCK_TYPE.PARAGRAPH;
  acc.pendingParagraphLines.push(line);

  return acc;
};

/**
 * Parses the block-level subset of markdown the Explain prompt emits —
 * paragraphs separated by blank lines and unordered lists where each item
 * begins with `- ` — into a typed array of blocks. Inline markup inside
 * each block is then parsed by `parseInlineMarkdown`. The Explain model
 * is instructed to only ever produce these block shapes, so headings,
 * ordered lists, links, tables and raw HTML are intentionally unsupported.
 */
export const parseMarkdownBlocks = text => {
  if (_.isEmpty(text)) {
    return [];
  }

  const acc = text.split('\n').reduce(processBlockLine, {
    blocks: [],
    pendingType: null,
    pendingParagraphLines: [],
    pendingListItems: [],
  });

  finalizeBlock(acc);

  return acc.blocks;
};

/**
 * Serialises the section's cached schema (an `ExpressionDefinitionDto`
 * loaded into Redux by the section's own schema action) into the
 * JSON string the Lender.Optimus.Ai `POST /ai-expressions/explain`
 * handler accepts in the `schemaJson` payload field. When supplied
 * the handler skips its server-side schema fetch and slices this
 * inline payload directly, which lets us add the Explain button to
 * areas whose schema isn't routed through SkynetAPI's
 * `ExpressionDefinitionController`. Returns `null` when the schema
 * isn't safely serialisable so the handler falls back to the
 * domain-based path.
 */
export const serializeSchemaForExplain = schemaParams => {
  if (_.isNil(schemaParams) || !_.isObject(schemaParams)) {
    return null;
  }

  try {
    return jsonStringify(schemaParams);
  } catch {
    return null;
  }
};

export const tryBuildExpressionTree = ({
  userFriendlyExpression,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
}) => {
  if (_.isEmpty(userFriendlyExpression)) {
    return '';
  }

  try {
    return buildExpressionTree(userFriendlyExpression, {
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    });
  } catch {
    return '';
  }
};

export const isCancelledError = error =>
  error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
